import { isWeb, isPhoneLayout } from './platform';

// Aspect ratio is the user-pickable axis. The canvas is always sized at
// the chosen aspect; cqw drives layout sizing inside it. Two options
// cover ~99% of monitors. 16:9 is the modern default, 16:10 wins back
// the vertical space taller monitors and laptops have. The user can
// also pin to "auto" which tracks whichever physical monitor the
// window is currently on (laptop screen vs. an external monitor).
export const ASPECT_AUTO = 'auto';
export const ASPECT_16_9 = '16:9';
export const ASPECT_16_10 = '16:10';
export const DEFAULT_ASPECT = ASPECT_16_9;

const ASPECT_KEY = 'valkenhall:aspect';
const VALID_PREFS = new Set([ASPECT_AUTO, ASPECT_16_9, ASPECT_16_10]);

export const DESIGN_WIDTH = 1600;
export const DESIGN_HEIGHT_16_9 = 900;
export const DESIGN_HEIGHT_16_10 = 1000;

// Halfway point between 16:10 (≈1.600) and 16:9 (≈1.778). Anything
// below this is closer to 16:10, anything above to 16:9. Ultrawide
// (21:9 ≈2.33) and 4:3 (≈1.333) both fall on the closer-of-two side
// of this midpoint, which is the right outcome. Closer-of-two beats
// "exact match required".
const ASPECT_MIDPOINT = 1.689;

function aspectFromDimensions(width, height) {
  if (!width || !height) return null;
  const ratio = width / height;
  return ratio < ASPECT_MIDPOINT ? ASPECT_16_10 : ASPECT_16_9;
}

// Cached snapshot of the display the window currently overlaps,
// updated by the SSE feed from the Electron main process. Falls back
// to `window.screen` when the main process is unreachable (web build,
// tests, dev tooling). That's a coarser signal because it can lag
// after a monitor handoff and can report the OS's idea of "primary"
// instead of the active screen.
let cachedDetectedAspect = null;

function detectFromWindowScreen() {
  if (typeof window === 'undefined' || !window.screen?.width || !window.screen?.height) {
    return DEFAULT_ASPECT;
  }
  return aspectFromDimensions(window.screen.width, window.screen.height) || DEFAULT_ASPECT;
}

export function detectMonitorAspect() {
  return cachedDetectedAspect || detectFromWindowScreen();
}

// Internal: update the cached detected aspect and broadcast a change
// when the user is on auto. Manual 16:9 / 16:10 preferences are not
// affected by monitor changes.
function updateDetectedAspect(next) {
  if (!next || next === cachedDetectedAspect) return;
  cachedDetectedAspect = next;
  if (typeof window !== 'undefined' && getAspectPreference() === ASPECT_AUTO) {
    window.dispatchEvent(new CustomEvent('valkenhall:aspect-change', { detail: getAspectRatio() }));
  }
}

// Connect to the main-process display feed. Best-effort: if the
// EventSource fails (web build, dev tooling without server) we silently
// stay with the window.screen fallback. EventSource auto-reconnects on
// transient errors, so we don't manage retries here.
function connectDisplaySSE() {
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;
  // No display feed on a static host, and EventSource retries a failed
  // connect forever, so don't even open one. window.screen carries us.
  if (isWeb()) return;
  let source;
  try {
    source = new EventSource('/api/display/events');
  } catch (_) {
    return;
  }
  source.onmessage = (e) => {
    try {
      const msg = JSON.parse(e.data);
      const display = msg?.display;
      if (!display) return;
      const next = aspectFromDimensions(display.width, display.height);
      if (next) updateDetectedAspect(next);
    } catch (_) { /* ignore malformed payload */ }
  };
}

if (typeof window !== 'undefined') {
  // Bun's test runner injects a window without EventSource by default,
  // so the connect() call no-ops there. In the renderer it kicks off the
  // SSE stream as a side effect of the module loading once.
  connectDisplaySSE();
}

export function getAspectPreference() {
  if (typeof window === 'undefined') return ASPECT_AUTO;
  const stored = window.localStorage.getItem(ASPECT_KEY);
  return VALID_PREFS.has(stored) ? stored : ASPECT_AUTO;
}

export function setAspectPreference(pref) {
  const safe = VALID_PREFS.has(pref) ? pref : ASPECT_AUTO;
  window.localStorage.setItem(ASPECT_KEY, safe);
  window.dispatchEvent(new CustomEvent('valkenhall:aspect-change', { detail: getAspectRatio() }));
  return safe;
}

// Backward-compat: older callers passed the resolved aspect directly.
// `setAspectPreference` is now the canonical setter.
export const setAspectRatio = setAspectPreference;

export function getAspectRatio() {
  const pref = getAspectPreference();
  if (pref === ASPECT_16_9 || pref === ASPECT_16_10) return pref;
  return detectMonitorAspect();
}

export function onAspectRatioChange(callback) {
  const handler = (e) => callback(e.detail);
  window.addEventListener('valkenhall:aspect-change', handler);
  return () => window.removeEventListener('valkenhall:aspect-change', handler);
}

export function getDesignHeight(aspect = getAspectRatio()) {
  return aspect === ASPECT_16_10 ? DESIGN_HEIGHT_16_10 : DESIGN_HEIGHT_16_9;
}

export const DESIGN_HEIGHT = DESIGN_HEIGHT_16_9; // legacy export. Defaults to 16:9

// Auto-fit floor. Below MIN_FIT_SCALE text drops under the 14px readability
// floor. Electron's BrowserWindow minWidth/minHeight is set so the window
// can't actually go below this, but the clamp is still defensive. There is
// no upper cap: at native 4K/8K the canvas should fill the screen the same
// way Hearthstone/WoW/Dota do. Pixel density makes physical size match.
export const MIN_FIT_SCALE = 0.85;

export function fitScale(vw, vh, dw = DESIGN_WIDTH, dh = getDesignHeight()) {
  if (vw <= 0 || vh <= 0) return 0;
  return Math.min(vw / dw, vh / dh);
}

export function clampedFitScale(vw, vh, dw = DESIGN_WIDTH, dh = getDesignHeight()) {
  const raw = fitScale(vw, vh, dw, dh);
  if (raw === 0) return 0;
  const clamped = Math.max(MIN_FIT_SCALE, raw);
  // Fail-safe: if clamping up to MIN_FIT_SCALE would push the canvas
  // beyond the viewport on either axis, fall back to the raw fit. The
  // 16:10 aspect on a 16:9-ish window is the canonical case. Electron
  // min (1360x765) already meets MIN_FIT_SCALE for 16:9, but a 16:10
  // canvas at the same min would be 1360x850 and overflow vertically,
  // which then clips the stage-root and traps content below the
  // visible viewport (scroll containers can't reach their bottom).
  if (dw * clamped > vw || dh * clamped > vh) return raw;
  return clamped;
}

export function stageDimensions(vw, vh, dw = DESIGN_WIDTH, dh = getDesignHeight()) {
  const s = clampedFitScale(vw, vh, dw, dh);
  return { width: dw * s, height: dh * s };
}

// Effective scale of the .app-canvas relative to its design dimensions.
// Useful when a JS layout calculation (e.g. VirtualList itemHeight) needs to
// scale in lockstep with the rest of the cqw-driven UI.
// Phone mode has no scaled canvas (mobile screens size in device rems)
// so the scale is identity there.
export function getCanvasScale() {
  if (isPhoneLayout()) return 1;
  return clampedFitScale(window.innerWidth, window.innerHeight);
}

// Subscribe to canvas-scale changes (window resize OR aspect change).
export function onCanvasScaleChange(callback) {
  const onResize = () => callback(getCanvasScale());
  const unsubAspect = onAspectRatioChange(() => callback(getCanvasScale()));
  window.addEventListener('resize', onResize);
  return () => {
    unsubAspect();
    window.removeEventListener('resize', onResize);
  };
}
